import useMintFeedback from 'core/hooks/useMintFeedback';
import BasicList from 'core/modules/components/lists/components/BasicList/BasicList';
import OptionalCheckboxGroup from 'core/modules/components/lists/components/OptionalCheckboxGroup/OptionalCheckboxGroup';
import UserItem from 'core/modules/components/lists/users/UserItem/UserItem';
import React, { useEffect, useState } from 'react';

/**
 * UserList - Lista de usuarios/alumnos. Envuelve el genérico v2 `BasicList`
 * (semántica `<ul>/<li>` accesible) dentro de un `OptionalCheckboxGroup` para la
 * selección múltiple opcional, y renderiza un `UserItem` por usuario.
 */

// `UserItem` es JS (props sin tipar): se aliasa a un componente permisivo para poder
// inyectar las props de dominio de cada usuario mediante spread.
const UserRow = UserItem as unknown as React.FC<Record<string, unknown>>;

export type User = Readonly<{ guid?: string; score?: unknown }> & Record<string, unknown>;

type UserListProps = Readonly<{
  assessment?: Record<string, unknown>;
  checkboxsRight?: boolean;
  chosenUser?: Record<string, unknown> | null;
  colorsPoints?: Record<string, unknown>;
  disabledInputPoints?: boolean;
  evaluationScale?: Record<string, unknown>;
  iconAction?: string;
  isBooleanScore?: boolean;
  isClickable?: boolean;
  isFeedbackActivity?: boolean;
  isLegendHidden?: boolean;
  isScore?: boolean;
  isScoreReadOnly?: boolean;
  legend?: string;
  managedSelectedUserList?: User[];
  maxSelected?: number;
  onAction?: (...args: unknown[]) => void;
  onChangeSelection?: (users: User[]) => void;
  onClick?: (user: User) => void;
  onScoreChange?: (...args: unknown[]) => void;
  scoreScale?: number;
  setChosenUser?: (user: User) => void;
  shouldSelectOnInput?: boolean;
  showCheckbox?: boolean;
  showLastNameFirst?: boolean;
  showPoints?: boolean;
  sizeAvatar?: string;
  users?: User[];
  usersDefaultSelected?: User[];
  usersSelectedProp?: User[];
  waitReturn?: boolean;
  withBorder?: boolean;
}>;

function UserList({
  assessment = {},
  checkboxsRight = false,
  chosenUser,
  colorsPoints,
  disabledInputPoints,
  evaluationScale,
  iconAction,
  isBooleanScore = false,
  isClickable = true,
  isFeedbackActivity = false,
  isLegendHidden,
  isScore = false,
  isScoreReadOnly = false,
  legend,
  managedSelectedUserList,
  maxSelected = 0,
  onAction,
  onChangeSelection = () => {},
  onClick = () => {},
  onScoreChange = () => {},
  scoreScale,
  setChosenUser = () => {},
  shouldSelectOnInput,
  showCheckbox,
  showLastNameFirst,
  showPoints,
  sizeAvatar,
  users = [],
  usersDefaultSelected = [],
  usersSelectedProp,
  waitReturn = false,
  withBorder,
}: UserListProps): JSX.Element {
  const [usersSelected, setUsersSelected] = useState<User[]>(usersSelectedProp || usersDefaultSelected);

  const { isMintFeedback } = useMintFeedback({ assessment });

  useEffect(() => {
    if (usersSelectedProp) {
      setUsersSelected(usersSelectedProp);
    }
  }, [usersSelectedProp]);

  useEffect(() => {
    if (managedSelectedUserList) {
      setUsersSelected(managedSelectedUserList);
    }
  }, [managedSelectedUserList]);

  function onSelected(user: User): void {
    if (isClickable) {
      onClick(user);
      setChosenUser(user);
    }
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>, guid: string): void {
    const selectedUser = users.filter((user) => user.guid === guid);
    if (selectedUser && selectedUser.length > 0) {
      let newUsers: User[] = [];
      if (e.target.checked) {
        if (maxSelected === 0 || usersSelected.length < maxSelected) {
          newUsers = [...usersSelected, selectedUser[0]];
        } else {
          newUsers = [...usersSelected];
        }
      } else {
        newUsers = usersSelected.filter((user) => user.guid !== guid);
      }
      setUsersSelected(newUsers);
      onChangeSelection(newUsers);
    }
  }

  const userItems = (
    <BasicList
      withBorder={withBorder}
      items={users.map((item) => (
        <UserRow
          key={item.guid}
          {...item}
          email={''}
          activeUser={chosenUser}
          onClick={() => onSelected(item)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>, guid: string) => onChange(e, guid)}
          showPoints={Boolean(showPoints) && !isFeedbackActivity && !isMintFeedback}
          checkboxsRight={checkboxsRight}
          showCheckbox={showCheckbox}
          isScore={isScore}
          isScoreReadOnly={isScoreReadOnly}
          checked={Boolean(usersSelected && usersSelected.find((studentSelected) => studentSelected.guid === item.guid))}
          onAction={onAction}
          iconAction={iconAction}
          isBooleanScore={isBooleanScore}
          onScoreChange={onScoreChange}
          shouldSelectOnInput={shouldSelectOnInput}
          sizeAvatar={sizeAvatar}
          setUsersSelected={setUsersSelected}
          usersSelected={usersSelected}
          score={item.score}
          scoreScale={scoreScale}
          disabledInputPoints={disabledInputPoints}
          colorsPoints={colorsPoints}
          isFeedbackActivity={isFeedbackActivity}
          item={item}
          assessment={assessment}
          evaluationScale={evaluationScale}
          waitReturn={waitReturn}
          showLastNameFirst={showLastNameFirst}
        />
      ))}
    />
  );

  return (
    <OptionalCheckboxGroup className="user-list" isLegendHidden={isLegendHidden} legend={legend}>
      {userItems}
    </OptionalCheckboxGroup>
  );
}

export default UserList;
